import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography, Menu, MenuItem, IconButton } from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { ModalSaveRecipe } from '@screens/Catalog/Recipes/ModalSaveRecipe';
import RecipesService from '@api/Recipes/RecipeCtrl';
import { buildInputsById, getRecipeCost, getRecipeMissingInputIds, Recipe } from '@api/Recipes/Recipe';
import InputsService from '@api/Inputs/InputCtrl';
import { formatUnitCost, Input } from '@api/Inputs/Input';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';

const ListRecipes: React.FunctionComponent = () => {
    const recipesService = useMemo(() => new RecipesService(), []);
    const inputsService = useMemo(() => new InputsService(), []);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [currentRecipe, setCurrentRecipe] = useState<Recipe | undefined>(undefined);
    const [filters, setFilters] = useState({
        search: ''
    });
    const modalSaveRecipe = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRecipe, setSelectedRecipe] = useState<Recipe | undefined>(undefined);

    const inputsById = useMemo(() => buildInputsById(inputs), [inputs]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Recipe) => {
        setAnchorEl(event.currentTarget);
        setSelectedRecipe(row);
    };

    const dispatch = useDispatch();

    /**
     * Abre la bitácora de este objeto (M2). Antes de esto, el catálogo escribía
     * renglones que **nadie podía ver**: no había puerta en ninguna de sus
     * pantallas.
     *
     * El snapshot se toma ANTES de cerrar el menú, que es donde se pierde la
     * selección, y el renglón de creación sale del propio documento (D8): no
     * cuesta una lectura.
     */
    const handleActivity = () => {
        const item = selectedRecipe;
        handleMenuClose();
        if (!item?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'recipe',
                sectionId: item.id,
                derived: buildCatalogOrigin(item, 'creó la receta', 'Receta creada')
            })
        );
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedRecipe) {
            setCurrentRecipe(selectedRecipe);
            modalSaveRecipe.toggle();
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedRecipe) {
            modalConfirmation.setModalData(
                'Eliminar receta',
                `¿Estás seguro de que deseas eliminar la receta "${selectedRecipe.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedRecipe?.id) {
            showLoader();
            try {
                await recipesService.deleteRecipe(selectedRecipe.id);
                showAlertMessage('Receta eliminada exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getData = async () => {
        showLoader();
        try {
            const [dataRecipes, dataInputs] = await Promise.all([
                recipesService.getRecipes(),
                inputsService.getInputs()
            ]);
            setRecipes(dataRecipes);
            setInputs(dataInputs);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddRecipe = () => {
        setCurrentRecipe(undefined);
        modalSaveRecipe.toggle();
    };

    const onSaveRecipe = async (recipe: Recipe) => {
        showLoader();
        try {
            const newData = await recipesService.saveRecipe(recipe);
            setCurrentRecipe(newData);
            showAlertMessage('Receta guardada exitosamente');
            await getData();
            modalSaveRecipe.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<Recipe>[]>(
        () => [
            {
                field: 'name',
                flex: 1.5,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Receta
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'items',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumos
                    </Typography>
                ),
                valueGetter: ({ row }) => row.items?.length || 0,
                renderCell: ({ row }) => {
                    const missing = getRecipeMissingInputIds(row.items, inputsById);
                    return (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>{row.items?.length || 0}</Typography>
                            {missing.length > 0 && (
                                <Chip size="small" color="error" label={`${missing.length} eliminado(s)`} />
                            )}
                        </Stack>
                    );
                }
            },
            {
                field: 'cost',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo (compra)
                    </Typography>
                ),
                // Costeo en vivo contra los insumos vigentes (el campo persistido puede quedar
                // desactualizado si un insumo cambió de costo después de guardar la receta)
                renderCell: ({ row }) => <Typography>{formatUnitCost(getRecipeCost(row.items, inputsById))}</Typography>
            },
            {
                field: 'availableForModifiers',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Modificadores
                    </Typography>
                ),
                renderCell: ({ row }) =>
                    row.availableForModifiers ? (
                        <Chip size="small" color="primary" label="Disponible" />
                    ) : (
                        <Chip size="small" variant="outlined" label="No" />
                    )
            },
            {
                field: 'status',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estatus
                    </Typography>
                ),
                renderCell: ({ row }) =>
                    row.status ? (
                        <Chip size="small" color="success" label="Activa" />
                    ) : (
                        <Chip size="small" color="default" label="Inactiva" />
                    )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        [inputsById]
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onOpenModalAddRecipe}
                            endIcon={<AddIcon />}
                        >
                            Nueva receta
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar receta"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={recipes}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setCurrentRecipe(cel.row as Recipe);
                    modalSaveRecipe.toggle();
                }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>
            <ModalSaveRecipe
                modalSaveRecipe={modalSaveRecipe}
                recipe={currentRecipe}
                inputsList={inputs}
                onSaveRecipe={onSaveRecipe}
            />
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};
export default ListRecipes;
